import { useEffect } from 'react';
import { apiClient } from './apiClient';

// Minimum time between two on-focus refreshes. With a 15-minute access
// token, anything below 5 minutes is wasted requests — the cookie is still
// fresh. 5 minutes catches both "user came back next morning" (cookie
// expired in background) and "user flipped to another tab and back two
// hours later".
const MIN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * On every tab focus / visibility-visible event, fire a cheap
 * `GET /auth/me`. Through the apiClient interceptor this silently rotates
 * the access cookie (and the underlying refresh row) if it's near expiry.
 *
 * Why both events: `focus` doesn't fire on mobile when the user switches
 * apps and comes back to the PWA; `visibilitychange` covers that case.
 * Both wired up; we throttle so concurrent events don't double-fire.
 */
export function useSessionRefreshOnFocus() {
  useEffect(() => {
    let last = 0;
    const tick = () => {
      const now = Date.now();
      if (now - last < MIN_INTERVAL_MS) return;
      last = now;
      apiClient.get('/auth/me').catch(() => null);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
